import { GlobalSearch, ISearchProvider, ISearchResult } from '@pages/app-shell/global-search';
import { ReactUtils } from '@shared/ui-react/ReactUtils';
import * as React from 'react';
import { mapRootStore, mapStateFacade, mapUI, mapWrapper } from '@pages/map/map';
import { FileTreeService } from '@shared/services/FileTreeService';
import { TreeNode } from '@shared/ui-native/Tree';
import { Events, EventsGlobal } from '@shared/Events';
import { SvgIconsB64 } from '@shared/map/MapIcons';


export const MapSearch = {
    open: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
        ReactUtils.safeRender(React.createElement(GlobalSearch, {
            treeData: mapRootStore.treeData,
            onClose: MapSearch.close,
            limit: 10,
            providers: [
                new SearchMapVehiclesProvider(),
                new SearchMapModelsProvider(),
                new SearchMapDocumentImageProvider(__('general.documents'), 'documents'),
                new SearchMapDocumentImageProvider(__('menu.photos'), 'images'),
                new SearchMapSavedViewsProvider(),
                new SearchTileLayersProvider()
            ]
        }), document.getElementById('react-portal-root'));
    },
    close: () => {
        ReactUtils.safeUnmountComponentAtNode(document.getElementById('react-portal-root'));
    },
    toggle: () => {
        if (document.querySelector('#global-search-wrapper')) {
            MapSearch.close();
        } else {
            MapSearch.open();
        }
    }
}

Events.on(EventsGlobal.MAIN_MENU_LOADED, () => {
    Events.on(EventsGlobal.GLOBAL_SEARCH_TOGGLE, MapSearch.toggle);
    (document.querySelector('.js-global-search-toggle') as HTMLDivElement).style.display = 'inline-flex';
});


class SearchMapDocumentImageProvider implements ISearchProvider {
    label = '';
    group = null;

    constructor(
        label: string,
        group: 'documents' | 'images'
    ) {
        this.label = label;
        this.group = group;
    }

    doSearch = async (query: string, limit: number = 20) => {
        let store: { items: { name: string, description: string, uuid: string, folderId?: number, icon?: string }[] } =
            this.group === 'documents' ? mapRootStore.documentsStore : mapRootStore.imagesStore;

        const foundFiles: ISearchResult[] = [];
        for (const file of store.items) {
            if (foundFiles.length >= limit) break;

            if (file.name.toLowerCase().includes(query) || (file.description ?? '').toLowerCase().includes(query)) {
                foundFiles.push({
                    group: this.group,
                    name: file.name,
                    uuid: file.uuid,
                    iconId: file.icon,
                    path: FileTreeService.getFolderPathFromIndex(mapRootStore.treeDataFoldersIndex, file.folderId)
                });
            }
        }
        return foundFiles;
    }

    getItemThumbnail = (item: ISearchResult) => {
        let url = null;
        if (this.group === 'documents') {
            url = SvgIconsB64[item.iconId] ?? SvgIconsB64.pdf;
        } else {
            url = `${kuura.getContextPath()}/api/1/image/${mapRootStore.imagesStore.getByUuid(item.uuid).id}_s.jpg`;
        }
        return <div style={{ backgroundImage: `url("${url}")`, backgroundSize: this.group === 'documents' ? '70%' : 'cover', width: '100%', height: '100%', backgroundRepeat: 'no-repeat', backgroundPosition: 'center' }}></div>
    }

    getItemActions = (item: ISearchResult) => {
        return [
            {
                label: __('js_global_search.actions.open_on_map'),
                action: (item: ISearchResult) => {
                    if (this.group === 'documents') {
                        mapWrapper.featureLayers.documents.openDocumentByUuid(item.uuid);
                    } else {
                        mapWrapper.featureLayers.images.openImageByUuid(item.uuid);
                    }
                    MapSearch.close();
                }
            },
            {
                label: __('js_global_search.actions.open_in_folder'),
                action: (item: ISearchResult) => {
                    mapUI.mapPanels.activatePanelById(this.group);
                    mapUI.mapPanels.panels[this.group].showInPanel(item.uuid);
                    MapSearch.close();
                }
            }
        ]
    }
}


class SearchMapModelsProvider implements ISearchProvider {
    label = __('menu.designs');
    group = 'models'

    doSearch = async (query: string, limit: number = 20) => {

        const nodes: TreeNode[] = [];
        mapUI.mapPanels.panels.models.tree.each(node => {
            if (node.isFile()) {
                nodes.push(node);
            }
        }, true, false)

        const foundNodes: ISearchResult[] = [];
        for (const node of nodes) {
            if (foundNodes.length >= limit) break;
            if (node.ajaxData.name.toLowerCase().includes(query)) {
                foundNodes.push({
                    group: this.group,
                    name: node.ajaxData.name,
                    uuid: node.uuid,
                    path: FileTreeService.getFolderPathFromIndex(mapRootStore.treeDataFoldersIndex, node.ajaxData.folderId)
                });
            }
        }

        return foundNodes;
    }

    getItemThumbnail = (item: ISearchResult) => {
        return <i className="iki iki-models" />;
    }

    getItemActions = (item: ISearchResult) => {
        const panel = mapUI.mapPanels.panels.models;
        const node = panel.tree.getByUuid(item.uuid);
        if (!node) return [];

        const isLoaded = panel.isModelLoaded(item.uuid);
        return [
            {
                label: __('js_global_search.actions.' + (isLoaded ? 'unload' : 'load')),
                action: (item: ISearchResult) => {
                    panel.setModelLoaded(item.uuid, !panel.isModelLoaded(item.uuid));
                    MapSearch.close();
                }
            },
            isLoaded ? {
                label: __('js_global_search.actions.fit'),
                action: (item: ISearchResult) => {
                    const node = panel.tree.getByUuid(item.uuid);
                    mapWrapper.fitExtent(panel.getNodeExtent(node));
                    MapSearch.close();
                }
            } : null,
            {
                label: __('js_global_search.actions.open_in_folder'),
                action: (item: ISearchResult) => {
                    mapUI.mapPanels.activatePanelById('models');
                    mapUI.mapPanels.panels.models.focusItem(item.uuid);
                    MapSearch.close();
                }
            }
        ].filter(Boolean)
    }
}

class SearchMapVehiclesProvider implements ISearchProvider {
    label = __('menu.vehicles');
    group = 'vehicles'

    doSearch = async (query: string, limit: number = 5) => {
        return mapRootStore.vehiclesStore.vehiclesFilteredGroupedByStatus.active.filter(v => {
            return v.name.toLowerCase().includes(query);
        }).splice(0, limit).map(v => {
            return {
                group: this.group,
                name: v.name,
                uuid: v.uuid
            }
        })
    }

    getItemThumbnail = (item: ISearchResult) => {
        return <i className="iki iki-equipment" />;
    }

    getItemActions = (item: ISearchResult) => {
        return [
            {
                label: __('js_global_search.actions.center_on_map'),
                action: (item: ISearchResult) => {
                    mapWrapper.featureLayers.vehicles.panToVehicle(item.uuid);
                    MapSearch.close();
                }
            }
        ]
    }
}


class SearchMapSavedViewsProvider implements ISearchProvider {
    label = __('menu.saved_views');
    group = 'saved_views'

    doSearch = async (query: string, limit: number = 20) => {
        const foundItems: ISearchResult[] = [];
        for (const item of mapStateFacade.store.itemsSimple) {
            if (foundItems.length >= limit) break;
            if (!item.name && !item.description) continue;

            if ((item.name ?? '').toLowerCase().includes(query) || (item.description ?? '').toLowerCase().includes(query)) {
                foundItems.push({
                    group: this.group,
                    name: item.name,
                    uuid: item.uuid,
                    path: FileTreeService.getFolderPath(mapRootStore.treeData, item.folderId)
                });
            }
        }

        return foundItems;
    }

    getItemThumbnail = (item: ISearchResult) => {
        return <i className="iki iki-saved-views" />;
    }

    getItemActions = (item: ISearchResult) => {
        return [
            {
                label: __('js_global_search.actions.load'),
                action: (item: ISearchResult) => {
                    const state = mapStateFacade.store.itemsSimple.find(i => i.uuid === item.uuid);
                    if (state) {
                        mapStateFacade.fetchStateAndRestore(state.uuid);
                    }
                    MapSearch.close();
                }
            },
            {
                label: __('js_global_search.actions.open_in_folder'),
                action: (item: ISearchResult) => {
                    const state = mapStateFacade.store.itemsSimple.find(i => i.uuid === item.uuid);
                    if (state) {
                        mapUI.mapPanels.activatePanelById('savedViews');
                        mapStateFacade.store.setSelectedFolderId(state.folderId, state.id);
                    }
                    MapSearch.close();
                }
            }
        ]
    }
}


class SearchTileLayersProvider implements ISearchProvider {
    label = __('js_map.tile_layer');
    group = 'layers';

    doSearch = async (query: string, limit: number = 20) => {
        const nodes: TreeNode[] = [];
        mapUI.mapPanels.panels.tileLayers.tree.each(node => {
            if (node.isFile()) {
                nodes.push(node);
            }
        }, true, false)

        const foundNodes: ISearchResult[] = [];
        for (const node of nodes) {
            if (foundNodes.length >= limit) break;

            if (node.ajaxData.name.toLowerCase().includes(query)) {
                foundNodes.push({
                    group: this.group,
                    name: node.ajaxData.name,
                    uuid: node.uuid,
                    path: FileTreeService.getFolderPathFromIndex(mapRootStore.treeDataFoldersIndex, node.ajaxData.folderId)
                });
            }
        }
        return foundNodes;
    }

    getItemThumbnail = (item: ISearchResult) => {
        return <i className="iki iki-map-layers" />;
    }

    getItemActions = (item: ISearchResult) => {
        const panel = mapUI.mapPanels.panels.tileLayers;
        const node = panel.tree.getByUuid(item.uuid);
        if (!node) return [];

        const isActive = panel.isLayerActive(item.uuid);
        const actions = [
            {
                label: __('js_global_search.actions.' + (isActive ? 'unload' : 'load')),
                action: (item: ISearchResult) => {
                    panel.setLayerActive(item.uuid, !panel.isLayerActive(item.uuid));
                    MapSearch.close();
                }
            }
        ]
        if (!isActive) {
            actions.push({
                label: __('js_global_search.actions.load_solo'),
                action: (item: ISearchResult) => {
                    panel.activateSolo(item.uuid);
                    MapSearch.close();
                }
            })
        }
        return actions;
    }
}
